import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
    fetchCustomPropertyNote,
    saveCustomPropertyNote,
    removeCustomPropertyNote,
} from "@/lib/api";

const extractNote = (res) => {
    if (typeof res === "string") return res;
    if (!res?.success) return "";
    if (typeof res.data === "string" && res.data) return res.data;
    if (typeof res.data?.note === "string") return res.data.note;
    if (typeof res.message === "string") return res.message;
    return "";
};

export const PropertyNoteDialog = ({ propertyId, open, onOpenChange, onNoteChange }) => {
    const [note, setNote] = useState("");
    const [hasNote, setHasNote] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const busy = loading || saving || deleting;

    useEffect(() => {
        if (!open || !propertyId) return;

        let cancelled = false;
        setNote("");
        setHasNote(false);
        setLoading(true);

        fetchCustomPropertyNote(propertyId)
            .then((res) => {
                if (cancelled) return;
                const text = extractNote(res);
                setNote(text);
                setHasNote(Boolean(text));
            })
            .catch(() => { })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, propertyId]);

    useEffect(() => {
        if (!open) setConfirmDeleteOpen(false);
    }, [open]);

    const onSave = async () => {
        const text = note.trim();
        if (!text) {
            toast.error("Qeyd boş ola bilməz.");
            return;
        }

        setSaving(true);
        try {
            const res = await saveCustomPropertyNote(propertyId, text);
            if (res && res.success === false) {
                toast.error(res.message || "Qeydi saxlamaq mümkün olmadı.");
                return;
            }
            toast.success(hasNote ? "Qeyd yeniləndi." : "Qeyd saxlanıldı.");
            onNoteChange?.(true);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.message || "Qeydi saxlamaq mümkün olmadı.");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        setConfirmDeleteOpen(false);
        setDeleting(true);
        try {
            const res = await removeCustomPropertyNote(propertyId);
            if (res && res.success === false) {
                toast.error(res.message || "Qeydi silmək mümkün olmadı.");
                return;
            }
            toast.success("Qeyd silindi.");
            setNote("");
            setHasNote(false);
            onNoteChange?.(false);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.message || "Qeydi silmək mümkün olmadı.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    data-testid="property-note-dialog"
                    className="sm:max-w-2xl"
                >
                    <DialogHeader>
                        <DialogTitle>Elan üçün qeydinizi yazın</DialogTitle>
                        <DialogDescription>
                            {hasNote
                                ? "Bu elan üçün əvvəl yazdığınız qeydi dəyişə və ya silə bilərsiniz."
                                : "Bu qeyd yalnız sizə görünəcək."}
                        </DialogDescription>
                    </DialogHeader>

                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        disabled={busy}
                        rows={10}
                        maxLength={1000}
                        placeholder={loading ? "Yüklənir..." : "Qeydinizi bura yazın..."}
                        data-testid="property-note-input"
                        className="w-full min-h-[220px] resize-y rounded-md border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                    />

                    <DialogFooter>
                        {hasNote ? (
                            <button
                                type="button"
                                onClick={() => setConfirmDeleteOpen(true)}
                                disabled={busy}
                                data-testid="property-note-delete"
                                className="px-4 py-2 rounded-md border border-red-500/50 text-red-600 text-sm font-semibold hover:bg-red-500/10 transition disabled:opacity-60"
                            >
                                {deleting ? "Silinir..." : "Sil"}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                disabled={busy}
                                className="px-4 py-2 rounded-md border border-border text-sm hover:bg-secondary transition disabled:opacity-60"
                            >
                                Ləğv et
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={busy}
                            data-testid="property-note-save"
                            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110 transition disabled:opacity-60"
                        >
                            {saving ? "Saxlanılır..." : "Yadda saxla"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
            >
                <AlertDialogContent data-testid="property-note-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Qeydi silmək istədiyinizdən əminsinizmi?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu qeyd həmişəlik silinəcək və geri qaytarıla bilməyəcək.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="property-note-delete-cancel">
                            Xeyr
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="property-note-delete-confirm-btn"
                            onClick={onDelete}
                            className="bg-red-600 text-white hover:bg-red-700"
                        >
                            Bəli, sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};